import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SchoolData, StaffMember, generateInviteCode, DEFAULT_DEPARTMENTS } from '@/lib/onboarding-store';
import { WizardStepLayout } from './WizardStepLayout';
import { UserPlus, X, Users, AlertTriangle, ClipboardList, CheckCircle2, Pencil, Info } from 'lucide-react';
import { PhoneInput } from '@/components/ui/PhoneInput';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { isDemoMode, showDemoLock } from '@/lib/demo/store';

// Demo mode caps the staff list at exactly 2 pre-seeded teachers. New
// additions show a popup instead of opening the add form. This keeps the
// floating inbox bounded and the marketing surface predictable.
function blockAddIfDemo(action: 'add' | 'csv'): boolean {
  if (!isDemoMode()) return false;
  showDemoLock({
    emoji: '👥',
    title: action === 'add' ? 'Two teachers in the demo' : 'Bulk import locked in demo',
    message:
      action === 'add'
        ? 'The demo ships with two pre-seeded teachers so the inbox stays focused. You can edit their names and details, emails stay fixed so notifications land in the demo inbox.'
        : 'Bulk imports are disabled in the demo. The two pre-seeded teachers are enough to walk through the approval and cover flow end-to-end.',
  });
  return true;
}

interface SchoolStep3Props {
  data: Partial<SchoolData>;
  onChange: (field: keyof SchoolData, value: unknown) => void;
  onNext: () => void;
  onBack: () => void;
  onReturnToReview?: () => void;
}

export function SchoolStep3Staff({
  data,
  onChange,
  onNext,
  onBack,
  onReturnToReview
}: SchoolStep3Props) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCsvForm, setShowCsvForm] = useState(false);
  // Pops the "what does an ideal spreadsheet look like" example dialog so admins
  // can see the expected format before pasting and avoid trial-and-error parsing.
  const [showSpreadsheetExample, setShowSpreadsheetExample] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvResult, setCsvResult] = useState<{ added: number; skipped: string[] } | null>(null);
  const [staffToDelete, setStaffToDelete] = useState<StaffMember | null>(null);
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', department: '', phone: '', subjects: [] as string[] });
  const [editEmailError, setEditEmailError] = useState('');
  const [newStaff, setNewStaff] = useState({
    firstName: '',
    lastName: '',
    email: '',
    department: '',
    phone: '',
    subjects: [] as string[],
  });
  const [emailError, setEmailError] = useState('');
  const [phoneDialogMsg, setPhoneDialogMsg] = useState('');

  const staff = data.staff || [];

  const hasUnsavedStaffDraft =
    (showAddForm && !!(newStaff.firstName.trim() || newStaff.lastName.trim() || newStaff.email.trim() || newStaff.phone.trim())) ||
    (showCsvForm && csvText.trim() !== '') ||
    editingMember !== null;

  // Use school-configured departments, falling back to defaults for schools that
  // onboarded before the departments field was introduced.
  const departments = [
    ...(data.departments?.length ? data.departments : DEFAULT_DEPARTMENTS),
  ].sort((a, b) => a.localeCompare(b)).concat('Other');

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const parsePhoneLocal = (v: string) => v.replace(/^\+\d+\s*/, '').replace(/\D/g, '');

  const addStaffMember = () => {
    if (!newStaff.firstName || !newStaff.lastName || !newStaff.email) return;
    if (!isValidEmail(newStaff.email)) {
      setEmailError('Please enter a valid email address (e.g. name@school.ac.uk)');
      setPhoneDialogMsg('');
      return;
    }
    // Phone is optional — but if any digits were entered they must be valid
    const phoneDigits = parsePhoneLocal(newStaff.phone);
    if (phoneDigits.length > 0) {
      const isUK = newStaff.phone.startsWith('+44');
      const valid = isUK ? phoneDigits.length === 10 : phoneDigits.length >= 7;
      if (!valid) {
        const msg = isUK
          ? 'UK phone numbers must be exactly 10 digits after the +44 country code (e.g. 7123456789).'
          : 'Phone number is too short. Please enter a valid number.';
        setEmailError(msg);
        setPhoneDialogMsg(msg);
        return;
      }
    }
    setEmailError('');
    setPhoneDialogMsg('');

    const member: StaffMember = {
      id: Date.now().toString(),
      firstName: newStaff.firstName,
      lastName: newStaff.lastName,
      email: newStaff.email,
      department: newStaff.department,
      phone: newStaff.phone,
      ...(newStaff.subjects.length > 0 ? { subjects: newStaff.subjects } : {}),
      inviteCode: generateInviteCode(),
      inviteSent: false,
      status: 'pending',
      invitedAt: Date.now(),
    };

    onChange('staff', [...staff, member]);
    setNewStaff({ firstName: '', lastName: '', email: '', department: '', phone: '', subjects: [] });
    setShowAddForm(false);
  };

  const removeStaff = (id: string) => {
    onChange('staff', staff.filter(s => s.id !== id));
    setStaffToDelete(null);
  };

  const openEdit = (member: StaffMember) => {
    setEditingMember(member);
    setEditForm({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      department: member.department || '',
      phone: member.phone || '',
      subjects: member.subjects ? [...member.subjects] : [],
    });
    setEditEmailError('');
    setShowAddForm(false);
    setShowCsvForm(false);
  };

  const saveEdit = () => {
    if (!editingMember) return;
    if (!editForm.firstName || !editForm.lastName || !editForm.email) return;
    if (!isValidEmail(editForm.email)) {
      setEditEmailError('Please enter a valid email address (e.g. name@school.ac.uk)');
      return;
    }
    setEditEmailError('');
    onChange('staff', staff.map(s =>
      s.id === editingMember.id
        ? { ...s, firstName: editForm.firstName, lastName: editForm.lastName, email: editForm.email, department: editForm.department, phone: editForm.phone, subjects: editForm.subjects.length > 0 ? editForm.subjects : undefined }
        : s
    ));
    setEditingMember(null);
  };

  const importFromCsv = () => {
    const lines = csvText.split('\n').map(l => l.trim()).filter(Boolean);
    const skipped: string[] = [];
    const existingEmails = new Set(staff.map(s => s.email.toLowerCase()));

    // First pass: group rows by email to merge duplicate entries into one teacher with all their subjects
    type ParsedRow = { firstName: string; lastName: string; email: string; department: string; phone: string };
    const byEmail = new Map<string, ParsedRow & { subjects: string[] }>();

    for (const line of lines) {
      const cols = line.includes('\t') ? line.split('\t') : line.split(',');
      const [col0, col1, col2, col3, col4] = cols.map(c => c.trim().replace(/^["']|["']$/g, ''));

      let firstName = col0 || '';
      let lastName = col1 || '';
      let email = col2 || '';
      let department = col3 || '';
      let phone = col4 || '';

      // Detect FullName, Email, Department, Phone format
      if (col1 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(col1)) {
        const nameParts = col0.split(' ');
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ');
        email = col1;
        department = col2 || '';
        phone = col3 || '';
      }

      if (!firstName || !email || !isValidEmail(email)) {
        skipped.push(line.slice(0, 40));
        continue;
      }

      const key = email.toLowerCase();

      if (byEmail.has(key)) {
        // Same email seen again — merge department as an additional subject
        const existing = byEmail.get(key)!;
        if (department && !existing.subjects.includes(department)) {
          existing.subjects.push(department);
        }
      } else if (existingEmails.has(key)) {
        skipped.push(`${email} (already in list)`);
      } else {
        byEmail.set(key, {
          firstName,
          lastName,
          email,
          department,
          phone,
          subjects: department ? [department] : [],
        });
        existingEmails.add(key);
      }
    }

    // Second pass: build StaffMember records
    const added: StaffMember[] = [];
    for (const row of byEmail.values()) {
      const subjects = row.subjects.length > 1 ? row.subjects : undefined;
      added.push({
        id: Date.now().toString() + Math.random().toString(36).slice(2),
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        department: row.department,
        phone: row.phone,
        ...(subjects ? { subjects } : {}),
        inviteCode: generateInviteCode(),
        inviteSent: false,
        status: 'pending',
        invitedAt: Date.now(),
      });
    }

    if (added.length > 0) {
      onChange('staff', [...staff, ...added]);
    }
    setCsvResult({ added: added.length, skipped });
    if (added.length > 0) {
      setCsvText('');
    }
  };

  return (
    <WizardStepLayout
      currentStep={5}
      totalSteps={8}
      title="Teaching Staff"
      subtitle="Add teachers who will be creating cover lessons"
      onNext={onNext}
      onBack={onBack}
      nextLabel={staff.length === 0 ? "I'll add staff later" : 'Continue'}
      onReturnToReview={onReturnToReview}
      unsavedDraftWarning={hasUnsavedStaffDraft
        ? (editingMember
            ? 'You’re editing a teacher but haven’t saved the changes yet.'
            : (showCsvForm && csvText.trim() !== '')
              ? 'You’ve pasted a staff list but haven’t imported it yet.'
              : 'You’ve started adding a teacher but haven’t saved it yet.')
        : undefined}
    >
      <div className="space-y-6">
        {/* Add teacher buttons */}
        {!showAddForm && !showCsvForm && (
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (blockAddIfDemo('add')) return;
                setShowAddForm(true);
              }}
              className="flex items-center justify-center gap-2 flex-1 py-3 px-4 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:brightness-110 transition-all shadow-sm"
            >
              <UserPlus className="w-4 h-4" />
              Add Teacher
            </button>
            <div className="relative flex-1 flex">
              <button
                onClick={() => {
                  if (blockAddIfDemo('csv')) return;
                  setShowCsvForm(true);
                  setCsvResult(null);
                }}
                className="flex items-center justify-center gap-2 flex-1 py-3 px-4 pr-9 rounded-xl border-2 border-dashed border-border text-muted-foreground font-semibold text-sm hover:border-primary/40 hover:text-primary transition-all"
              >
                <ClipboardList className="w-4 h-4" />
                Paste from spreadsheet
              </button>
              {/* Info icon — shows an example of the ideal spreadsheet layout */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowSpreadsheetExample(true); }}
                className="absolute top-1/2 right-2 -translate-y-1/2 p-1.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                aria-label="Show example spreadsheet format"
                title="See example spreadsheet"
              >
                <Info className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* CSV paste form */}
        <AnimatePresence>
          {showCsvForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-5 bg-muted/30 rounded-2xl border border-border space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">Paste from spreadsheet</h3>
                  <button onClick={() => { setShowCsvForm(false); setCsvText(''); setCsvResult(null); }} className="p-1 hover:bg-muted rounded-lg transition-colors">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Copy rows from Excel or Google Sheets and paste below. Each row should contain:
                  <br />
                  <strong className="text-foreground">First Name, Last Name, Email, Department, Phone</strong> (tab or comma separated)
                  <br />
                  Or: <strong className="text-foreground">Full Name, Email, Department, Phone</strong>
                  <br />
                  <span className="text-muted-foreground/70">Department and Phone are optional. Rows with the same email are automatically merged. Each department becomes a subject for that teacher.</span>
                </p>
                <textarea
                  value={csvText}
                  onChange={(e) => { setCsvText(e.target.value); setCsvResult(null); }}
                  placeholder={"John\tSmith\tjohn.smith@school.ac.uk\tMathematics\t+447123456789\nJane\tDoe\tjane.doe@school.ac.uk\tEnglish"}
                  rows={6}
                  className="input-premium w-full font-mono text-xs resize-y"
                />
                {csvResult && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-3 rounded-xl border text-sm space-y-1 ${csvResult.added > 0 ? 'bg-success/5 border-success/20' : 'bg-muted border-border'}`}
                  >
                    {csvResult.added > 0 && (
                      <p className="flex items-center gap-1.5 text-success font-medium">
                        <CheckCircle2 className="w-4 h-4" />
                        {csvResult.added} teacher{csvResult.added !== 1 ? 's' : ''} added successfully
                      </p>
                    )}
                    {csvResult.skipped.length > 0 && (
                      <div>
                        <p className="text-muted-foreground font-medium">{csvResult.skipped.length} row{csvResult.skipped.length !== 1 ? 's' : ''} skipped (invalid or duplicate):</p>
                        <ul className="mt-1 space-y-0.5 pl-3">
                          {csvResult.skipped.map((s, i) => (
                            <li key={i} className="text-xs text-muted-foreground list-disc list-inside truncate">{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </motion.div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={importFromCsv}
                    disabled={!csvText.trim()}
                    className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Import Teachers
                  </button>
                  {csvResult?.added ? (
                    <button
                      onClick={() => { setShowCsvForm(false); setCsvText(''); setCsvResult(null); }}
                      className="px-4 py-2 rounded-xl bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors"
                    >
                      Done
                    </button>
                  ) : null}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Add form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-6 bg-muted/30 rounded-2xl border border-border space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">New Teacher</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setNewStaff({ firstName: '', lastName: '', email: '', department: '', phone: '', subjects: [] })}
                      className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg hover:bg-muted transition-colors"
                    >
                      Clear Form
                    </button>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="p-1 hover:bg-muted rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={newStaff.firstName}
                    onChange={(e) => setNewStaff({ ...newStaff, firstName: e.target.value })}
                    placeholder="First name *"
                    className="input-premium"
                  />
                  <input
                    type="text"
                    value={newStaff.lastName}
                    onChange={(e) => setNewStaff({ ...newStaff, lastName: e.target.value })}
                    placeholder="Last name *"
                    className="input-premium"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => { setNewStaff({ ...newStaff, email: e.target.value }); setEmailError(''); }}
                    onBlur={() => { if (newStaff.email && !isValidEmail(newStaff.email)) setEmailError('Please enter a valid email address (e.g. name@school.ac.uk)'); }}
                    placeholder="School email *"
                    className={`input-premium w-full ${emailError ? 'border-destructive' : ''}`}
                  />
                  {emailError && <p className="text-xs text-destructive mt-1">{emailError}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    value={newStaff.department}
                    onChange={(e) => setNewStaff({ ...newStaff, department: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">Select department</option>
                    {departments.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <PhoneInput
                    value={newStaff.phone}
                    onChange={(v) => setNewStaff({ ...newStaff, phone: v })}
                    placeholder="Phone (optional)"
                  />
                </div>

                {(data.subjects || []).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Subjects taught <span className="font-normal">(optional. Leave blank for all school subjects)</span></p>
                    <div className="flex flex-wrap gap-1.5">
                      {(data.subjects || []).map(subject => {
                        const active = newStaff.subjects.includes(subject);
                        return (
                          <button
                            key={subject}
                            type="button"
                            onClick={() => setNewStaff(prev => ({
                              ...prev,
                              subjects: active
                                ? prev.subjects.filter(s => s !== subject)
                                : [...prev.subjects, subject],
                            }))}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                            }`}
                          >
                            {subject}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <button
                    onClick={addStaffMember}
                    disabled={!newStaff.firstName || !newStaff.lastName || !newStaff.email}
                    className="btn-primary w-full"
                  >
                    Add Teacher
                  </button>
                  {(!newStaff.firstName || !newStaff.lastName || !newStaff.email) && (
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Please fill in first name, last name, and email to add a teacher
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit form */}
        <AnimatePresence>
          {editingMember && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-6 bg-primary/5 rounded-2xl border-2 border-primary/20 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">Edit Teacher</h3>
                  <button onClick={() => setEditingMember(null)} className="p-1 hover:bg-muted rounded-lg transition-colors">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} placeholder="First name *" className="input-premium" />
                  <input type="text" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} placeholder="Last name *" className="input-premium" />
                </div>

                <div>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => { setEditForm({ ...editForm, email: e.target.value }); setEditEmailError(''); }}
                    onBlur={() => { if (editForm.email && !isValidEmail(editForm.email)) setEditEmailError('Please enter a valid email address (e.g. name@school.ac.uk)'); }}
                    placeholder="School email *"
                    className={`input-premium w-full ${editEmailError ? 'border-destructive' : ''}`}
                  />
                  {editEmailError && <p className="text-xs text-destructive mt-1">{editEmailError}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="input-premium">
                    <option value="">Select department</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <PhoneInput value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} placeholder="Phone (optional)" />
                </div>

                {(data.subjects || []).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Subjects taught <span className="font-normal">(optional)</span></p>
                    <div className="flex flex-wrap gap-1.5">
                      {(data.subjects || []).map(subject => {
                        const active = editForm.subjects.includes(subject);
                        return (
                          <button key={subject} type="button"
                            onClick={() => setEditForm(prev => ({ ...prev, subjects: active ? prev.subjects.filter(s => s !== subject) : [...prev.subjects, subject] }))}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'}`}
                          >{subject}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  onClick={saveEdit}
                  disabled={!editForm.firstName || !editForm.lastName || !editForm.email}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Staff list */}
        {staff.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>{staff.length} teacher{staff.length !== 1 ? 's' : ''} added</span>
            </div>

            <div className="space-y-2">
              {staff.map((member) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 bg-card rounded-xl border border-border"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary">
                        {member.firstName[0]}{member.lastName[0]}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {member.email}
                      </p>
                      {member.subjects && member.subjects.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {member.subjects.map(s => (
                            <span key={s} className="px-1.5 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-medium">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => openEdit(member)}
                      className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                      title="Edit teacher"
                    >
                      <Pencil className="w-4 h-4 text-primary" />
                    </button>
                    <button
                      onClick={() => setStaffToDelete(member)}
                      className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                      title="Remove teacher"
                    >
                      <X className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state info */}
        {staff.length === 0 && !showAddForm && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-foreground mb-1">No teachers added yet</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Add teachers now or skip this step and invite them later from your admin panel.
            </p>
          </div>
        )}
      </div>

      {/* Phone validation dialog */}
      <AlertDialog open={!!phoneDialogMsg} onOpenChange={(open) => !open && setPhoneDialogMsg('')}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Invalid Phone Number
            </AlertDialogTitle>
            <AlertDialogDescription>{phoneDialogMsg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setPhoneDialogMsg('')}>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!staffToDelete} onOpenChange={(open) => !open && setStaffToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Remove Teacher
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{staffToDelete?.firstName} {staffToDelete?.lastName}</strong>?
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => staffToDelete && removeStaff(staffToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Example spreadsheet format — opened from the info icon next to "Paste from spreadsheet" */}
      <AlertDialog open={showSpreadsheetExample} onOpenChange={(open) => setShowSpreadsheetExample(open)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Example spreadsheet format</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm">
                  Use this layout in Excel, Google Sheets, or Numbers. Copy the rows (no header needed) and paste into the textarea.
                </p>
                {/* Excel-style preview rendered with CSS — no image file dependency */}
                <div className="rounded-lg border border-border overflow-hidden bg-white dark:bg-card">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono">
                      <thead>
                        <tr className="bg-[#1f6e43] text-white">
                          <th className="px-2 py-1.5 text-left font-semibold border-r border-[#155233]">First Name</th>
                          <th className="px-2 py-1.5 text-left font-semibold border-r border-[#155233]">Last Name</th>
                          <th className="px-2 py-1.5 text-left font-semibold border-r border-[#155233]">Email</th>
                          <th className="px-2 py-1.5 text-left font-semibold border-r border-[#155233]">Department</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-white dark:bg-card">
                          <td className="px-2 py-1.5 border-r border-b border-border">John</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">Smith</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">john.smith@school.ac.uk</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">Mathematics</td>
                          <td className="px-2 py-1.5 border-b border-border">+447123456789</td>
                        </tr>
                        <tr className="bg-muted/30">
                          <td className="px-2 py-1.5 border-r border-b border-border">Jane</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">Doe</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">jane.doe@school.ac.uk</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">English</td>
                          <td className="px-2 py-1.5 border-b border-border">+447987654321</td>
                        </tr>
                        <tr className="bg-white dark:bg-card">
                          <td className="px-2 py-1.5 border-r border-b border-border">Ada</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">Khan</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">a.khan@school.ac.uk</td>
                          <td className="px-2 py-1.5 border-r border-b border-border">Science</td>
                          <td className="px-2 py-1.5 border-b border-border"></td>
                        </tr>
                        <tr className="bg-muted/30">
                          <td className="px-2 py-1.5 border-r border-border">Marcus</td>
                          <td className="px-2 py-1.5 border-r border-border">Allen</td>
                          <td className="px-2 py-1.5 border-r border-border">m.allen@school.ac.uk</td>
                          <td className="px-2 py-1.5 border-r border-border">PE &amp; Sport</td>
                          <td className="px-2 py-1.5"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="text-xs space-y-1.5 text-foreground">
                  <p className="font-semibold">Tips for a clean import:</p>
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    <li><strong>No header row needed</strong>, paste just the teacher rows.</li>
                    <li>Columns can be separated by tabs (Excel default) or commas.</li>
                    <li><strong>Email is required.</strong> Department and Phone are optional.</li>
                    <li>If you only have <strong>Full Name</strong> (not split), the importer still works. Use: <span className="font-mono">Full Name, Email, Department, Phone</span>.</li>
                    <li>Rows sharing the same email are merged automatically. Each department becomes a subject the teacher can teach.</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowSpreadsheetExample(false)}>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WizardStepLayout>
  );
}
